import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useUserProfileStore } from '@/lib/store/userProfileStore';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import Alert from '@/components/common/Alert';

const userProfileSchema = z.object({
  full_name: z.string()
    .min(3, { message: "Full name must be at least 3 characters" })
    .max(100, { message: "Full name is too long" }),
  phone_number: z.string()
    .optional()
    .refine(val => !val || /^\+?[0-9\s\-()]{7,20}$/.test(val), {
      message: "Phone number format is invalid"
    }),
  job_title: z.string()
    .optional(),
  department: z.string()
    .optional()
});

type UserProfileFormValues = z.infer<typeof userProfileSchema>;

export const UserProfileForm = () => {
  const { userProfile, isLoading, error, fetchUserProfile, updateUserProfile } = useUserProfileStore();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<UserProfileFormValues>({
    resolver: zodResolver(userProfileSchema),
    defaultValues: {
      full_name: '',
      phone_number: '',
      job_title: '',
      department: ''
    }
  });
  
  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);
  
  useEffect(() => {
    if (userProfile) {
      reset({
        full_name: userProfile.full_name,
        phone_number: userProfile.phone_number || '',
        job_title: userProfile.job_title || '',
        department: userProfile.department || ''
      });
    }
  }, [userProfile, reset]);
  
  const onSubmit = async (data: UserProfileFormValues) => {
    setIsSubmitting(true);
    setSuccessMessage(null);
    
    try {
      await updateUserProfile(data);
      setSuccessMessage('Profile updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading && !userProfile) {
    return (
      <Card className="p-6">
        <div className="flex justify-center py-8">
          <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
        </div>
      </Card>
    );
  }
  
  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">Personal Information</h2>
      
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert variant="success" className="mb-4">
          {successMessage}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Full Name"
          {...register('full_name')}
          error={errors.full_name?.message}
          required
        />
        
        <Input
          label="Phone Number"
          {...register('phone_number')}
          error={errors.phone_number?.message}
          placeholder="+1 (123) 456-7890"
        />
        
        <Input
          label="Job Title"
          {...register('job_title')}
          error={errors.job_title?.message}
          placeholder="e.g. Registered Nurse"
        />
        
        <Input
          label="Department"
          {...register('department')}
          error={errors.department?.message}
          placeholder="e.g. Emergency Department"
        />
        
        <div className="pt-4 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Card>
  );
}; 